import {
  type ChildId,
  type Collection,
  type CollectionId,
  type ContentItem,
  parseYouTubeVideoId,
  resolveImportSource,
  type YouTubeVideo,
} from '@kidshub/contracts';
import { type FormEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { apiClient } from '../lib/api-client.js';
import { toErrorKey } from '../lib/error-code.js';
import { useResource } from '../lib/use-resource.js';
import { AppShell } from '../ui/app-shell.js';
import { Button, ButtonLink, Card, Chip } from '../ui/kit.js';
import { ARTWORK_KEYS, type ArtworkKey, DEFAULT_ARTWORK_KEY } from './artwork-keys.js';
import { FlowSteps, toImportSourceHandover } from './import-source-page.js';
import { SourceBadge } from './source-badge.js';
import { YouTubeDisclosure } from './youtube-disclosure.js';
import { YouTubePreview } from './youtube-preview.js';

/**
 * Adding content, in three steps that mirror the decision a parent is making.
 *
 * Where is it from → look at it yourself → who is it for. The middle step is
 * `SPEC.md` §11.1 and is not skippable; the last step is where the item stops
 * belonging to nobody.
 *
 * Nothing is written until the final confirm. A parent who backs out halfway
 * has added nothing, which matters because the failure mode of a wizard that
 * saves as it goes is a half-approved item sitting in a library.
 */

type Stage =
  | { name: 'SOURCE' }
  | { name: 'PREVIEW'; video: YouTubeVideo }
  | { name: 'DESTINATION'; video: YouTubeVideo };

interface Draft {
  title: string;
  artworkKey: ArtworkKey;
  collectionId: CollectionId | null;
  childIds: ChildId[];
}

export const AddContentPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const children = useResource('children', () => apiClient.children.list());
  const collections = useResource('collections', () => apiClient.collections.list());

  const [stage, setStage] = useState<Stage>({ name: 'SOURCE' });
  const [url, setUrl] = useState('');
  const [draft, setDraft] = useState<Draft>({
    title: '',
    artworkKey: DEFAULT_ARTWORK_KEY,
    collectionId: null,
    childIds: [],
  });
  const [errorKey, setErrorKey] = useState<string | null>(null);
  const [isBusy, setIsBusy] = useState(false);

  const resolve = async (event: FormEvent) => {
    event.preventDefault();
    setErrorKey(null);

    // Checked here as well as on the server, using the contract's own parser.
    // A parent who pasted the wrong thing hears about it immediately rather
    // than after a round trip.
    if (parseYouTubeVideoId(url) === null) {
      // A channel or a playlist is not a mistake, it is the other way in: the
      // import screen lists what is there and lets the parent choose. Sending
      // the link on is kinder than calling it "not a YouTube link".
      if (resolveImportSource(url) !== null) {
        void navigate('/library/import', { state: toImportSourceHandover(url) });
        return;
      }

      setErrorKey('error.INVALID_YOUTUBE_URL');
      return;
    }

    setIsBusy(true);
    try {
      const video = await apiClient.youtube.resolve({ url });
      setDraft((previous) => ({ ...previous, title: video.title.slice(0, 80) }));
      setStage({ name: 'PREVIEW', video });
    } catch (error) {
      setErrorKey(toErrorKey(error));
    } finally {
      setIsBusy(false);
    }
  };

  const confirm = async (video: YouTubeVideo) => {
    setErrorKey(null);
    setIsBusy(true);

    try {
      const item = await apiClient.catalog.create({
        providerId: 'YOUTUBE',
        contentType: 'VIDEO',
        title: draft.title,
        artworkKey: draft.artworkKey,
        sourceKey: video.videoId,
        ...(video.durationSeconds === null ? {} : { durationSeconds: video.durationSeconds }),
      });

      // Approval is a separate call per child on purpose: the contract models
      // opening an item to somebody as its own act, not as a field on creation.
      for (const childId of draft.childIds) {
        await apiClient.catalog.setApproval({
          contentItemId: item.id,
          childId,
          isApproved: true,
        });
      }

      if (draft.collectionId !== null) {
        await addToCollection(draft.collectionId, item);
      }

      void navigate('/library', { replace: true });
    } catch (error) {
      setErrorKey(toErrorKey(error));
      setIsBusy(false);
    }
  };

  const addToCollection = async (collectionId: CollectionId, item: ContentItem) => {
    const existing = collections.resource.status === 'ready' ? collections.resource.data : null;
    const target = existing?.collections.find((entry) => entry.id === collectionId);
    await apiClient.collections.setItems({
      collectionId,
      itemIds: [...(target?.itemIds ?? []), item.id],
    });
  };

  const toggleChild = (childId: ChildId) => {
    setDraft((previous) => ({
      ...previous,
      childIds: previous.childIds.includes(childId)
        ? previous.childIds.filter((id) => id !== childId)
        : [...previous.childIds, childId],
    }));
  };

  const toSource = () => setStage({ name: 'SOURCE' });
  const stepIndex = stage.name === 'SOURCE' ? 0 : stage.name === 'PREVIEW' ? 1 : 2;
  const allCollections =
    collections.resource.status === 'ready'
      ? collections.resource.data.collections
      : ([] as Collection[]);
  const familyChildren =
    children.resource.status === 'ready' ? children.resource.data.children : [];
  const errorLine =
    errorKey === null ? null : (
      <p className="help err" role="alert">
        {t(errorKey)}
      </p>
    );

  return (
    <AppShell title={t('addContent.title')} subtitle={t('addContent.subtitle')}>
      <FlowSteps
        current={stepIndex}
        steps={[
          {
            label: t('addContent.steps.source'),
            ...(stage.name === 'SOURCE' ? {} : { onSelect: toSource }),
          },
          {
            label: t('addContent.steps.preview'),
            ...(stage.name === 'DESTINATION'
              ? { onSelect: () => setStage({ name: 'PREVIEW', video: stage.video }) }
              : {}),
          },
          { label: t('addContent.steps.destination') },
        ]}
      />

      <div className="import-layout">
        {stage.name === 'SOURCE' ? (
          <>
            <div className="import-main">
              <Card title={t('addContent.sourceHeading')} meta={t('addContent.sourceBody')}>
                <form onSubmit={(event) => void resolve(event)}>
                  <div className="field">
                    <label htmlFor="add-content-url">{t('addContent.youTubeLink')}</label>
                    <div className="url-row">
                      <input
                        id="add-content-url"
                        className="input"
                        type="text"
                        inputMode="url"
                        autoComplete="off"
                        spellCheck={false}
                        required
                        value={url}
                        onChange={(event) => setUrl(event.target.value)}
                        placeholder={t('addContent.youTubeLinkPlaceholder')}
                      />
                      <Button type="submit" variant="primary" disabled={isBusy}>
                        {t(isBusy ? 'addContent.looking' : 'common.continue')}
                      </Button>
                    </div>
                    <p className="help">{t('addContent.linkHelp')}</p>
                    {errorLine}
                  </div>
                </form>
              </Card>
            </div>

            <aside className="import-side">
              <Card title={t('addContent.channelHeading')} meta={t('addContent.channelBody')}>
                <ButtonLink variant="secondary" icon="plus" block to="/library/import">
                  {t('nav.addChannel')}
                </ButtonLink>
                <p className="help import-later">{t('addContent.localFilesLater')}</p>
              </Card>
            </aside>
          </>
        ) : null}

        {stage.name === 'PREVIEW' ? (
          <>
            <div className="import-main">
              <Card title={t('addContent.previewHeading')} meta={t('addContent.previewHint')}>
                <YouTubePreview video={stage.video} />
              </Card>
            </div>

            <aside className="import-side">
              <YouTubeDisclosure />
              <div className="import-actions">
                <Button variant="ghost" onClick={toSource}>
                  {t('common.back')}
                </Button>
                <Button
                  variant="primary"
                  onClick={() => setStage({ name: 'DESTINATION', video: stage.video })}
                >
                  {t('common.continue')}
                </Button>
              </div>
            </aside>
          </>
        ) : null}

        {stage.name === 'DESTINATION' ? (
          <>
            <div className="import-main">
              <Card
                title={t('addContent.destinationHeading')}
                meta={t('addContent.destinationHint')}
              >
                <div className="import-picked">
                  <b>{stage.video.title}</b>
                  <SourceBadge providerId="YOUTUBE" contentType="VIDEO" />
                </div>

                <div className="opt-group">
                  <div className="field">
                    <label htmlFor="add-content-title">{t('addContent.titleOnTablet')}</label>
                    <input
                      id="add-content-title"
                      className="input"
                      type="text"
                      required
                      maxLength={80}
                      value={draft.title}
                      onChange={(event) => setDraft({ ...draft, title: event.target.value })}
                    />
                  </div>
                </div>

                <div className="opt-group">
                  <h3>{t('addContent.artwork')}</h3>
                  <div className="chips">
                    {ARTWORK_KEYS.map((key) => (
                      <Chip
                        key={key}
                        selected={key === draft.artworkKey}
                        onToggle={() => setDraft({ ...draft, artworkKey: key })}
                      >
                        {t(`artwork.${key}`)}
                      </Chip>
                    ))}
                  </div>
                </div>

                <div className="opt-group">
                  <h3>{t('addContent.collection')}</h3>
                  <div className="chips">
                    <Chip
                      selected={draft.collectionId === null}
                      onToggle={() => setDraft({ ...draft, collectionId: null })}
                    >
                      {t('addContent.noCollection')}
                    </Chip>
                    {allCollections.map((collection) => (
                      <Chip
                        key={collection.id}
                        selected={draft.collectionId === collection.id}
                        onToggle={() => setDraft({ ...draft, collectionId: collection.id })}
                      >
                        {collection.title}
                      </Chip>
                    ))}
                  </div>
                </div>

                <div className="opt-group">
                  <h3>{t('addContent.whoCanSee')}</h3>
                  <div className="chips">
                    {familyChildren.map((entry) => (
                      <Chip
                        key={entry.child.id}
                        selected={draft.childIds.includes(entry.child.id)}
                        onToggle={() => toggleChild(entry.child.id)}
                      >
                        {entry.child.name}
                      </Chip>
                    ))}
                  </div>
                  {draft.childIds.length === 0 ? (
                    <p className="help">{t('addContent.nobodyYet')}</p>
                  ) : null}
                </div>
              </Card>
            </div>

            <aside className="import-side">
              {/*
                Said again here, and not only on the preview step. §11.3 requires
                the parent to be told when adding, and this is the button that
                adds: a disclosure two screens back has been read by someone who
                was still deciding, not by someone about to commit.
              */}
              <YouTubeDisclosure />
              <div className="import-actions">
                <Button
                  variant="ghost"
                  onClick={() => setStage({ name: 'PREVIEW', video: stage.video })}
                >
                  {t('common.back')}
                </Button>
                <Button
                  variant="primary"
                  disabled={isBusy || draft.title.trim() === ''}
                  onClick={() => void confirm(stage.video)}
                >
                  {t(isBusy ? 'addContent.adding' : 'addContent.confirm')}
                </Button>
              </div>
              {errorLine}
            </aside>
          </>
        ) : null}
      </div>
    </AppShell>
  );
};
